import { useEffect, useState } from "react";
import { User, Session, AuthenticatorAssuranceLevels } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin, checkIsRep, checkIsGuide } from "@/lib/auth";

export type MfaMethodType = "totp" | "email" | null;

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isRep, setIsRep] = useState(false);
  const [isGuide, setIsGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mfaLevel, setMfaLevel] = useState<AuthenticatorAssuranceLevels | null>(null);
  const [mfaEnrolled, setMfaEnrolled] = useState<boolean | null>(null);
  const [mfaMethod, setMfaMethod] = useState<MfaMethodType>(null);

  useEffect(() => {
    let isMounted = true;
    let roleCheckToken = 0;

    const checkMfa = async (userId: string) => {
      try {
        // Check profile for mfa_method
        const { data: profile } = await supabase
          .from("profiles")
          .select("mfa_method")
          .eq("id", userId)
          .single();

        if (!isMounted) return;

        if (profile?.mfa_method === "email") {
          setMfaMethod("email");
          setMfaEnrolled(true);
          // For email OTP, we use a session flag stored after verification
          const emailMfaVerified = sessionStorage.getItem(`email_mfa_verified_${userId}`);
          setMfaLevel(emailMfaVerified === "true" ? "aal2" : "aal1");
          return;
        }

        // Default: check TOTP
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!isMounted) return;
        if (aalData) {
          setMfaLevel(aalData.currentLevel);
          const { data: factorData } = await supabase.auth.mfa.listFactors();
          if (!isMounted) return;
          const hasVerifiedFactor = factorData?.totp?.some((f) => f.status === "verified") ?? false;
          setMfaEnrolled(hasVerifiedFactor);
          setMfaMethod(hasVerifiedFactor ? "totp" : null);
        }
      } catch {
        if (isMounted) {
          setMfaLevel(null);
          setMfaEnrolled(null);
          setMfaMethod(null);
        }
      }
    };

    const applySession = (session: Session | null) => {
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const token = ++roleCheckToken;
        setLoading(true);

        checkMfa(session.user.id);

        setTimeout(() => {
          if (!isMounted || token !== roleCheckToken) return;

          Promise.all([
            checkIsAdmin(session.user.id),
            checkIsRep(session.user.id),
            checkIsGuide(session.user.id),
          ])
            .then(([admin, rep, guide]) => {
              if (!isMounted || token !== roleCheckToken) return;
              setIsAdmin(admin);
              setIsRep(rep);
              setIsGuide(guide);
            })
            .catch(() => {
              if (!isMounted || token !== roleCheckToken) return;
              setIsAdmin(false);
              setIsRep(false);
              setIsGuide(false);
            })
            .finally(() => {
              if (!isMounted || token !== roleCheckToken) return;
              setLoading(false);
            });
        }, 0);
      } else {
        setIsAdmin(false);
        setIsRep(false);
        setIsGuide(false);
        setMfaLevel(null);
        setMfaEnrolled(null);
        setMfaMethod(null);
        setLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Function to mark email MFA as verified for this session
  const markEmailMfaVerified = () => {
    if (user) {
      sessionStorage.setItem(`email_mfa_verified_${user.id}`, "true");
      setMfaLevel("aal2");
    }
  };

  return { user, session, isAdmin, isRep, isGuide, loading, mfaLevel, mfaEnrolled, mfaMethod, markEmailMfaVerified };
}
